import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { userService, ProfileData } from "../../services/userService";
import {
	FiUser,
	FiEdit2,
	FiSave,
	FiLock,
	FiShield,
	FiAlertCircle,
} from "react-icons/fi";

const Profile: React.FC = () => {
	const { user, checkAuth } = useAuth();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [formData, setFormData] = useState<ProfileData>({
		firstName: "",
		lastName: "",
		email: "",
	});

	useEffect(() => {
		if (user && user.name) {
			setFormData({
				firstName: user.name.firstName || "",
				lastName: user.name.lastName || "",
				email: user.email || "",
			});
		}
	}, [user]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.firstName || !formData.lastName) {
			toast.error("名前を入力してください");
			return;
		}

		try {
			setIsSubmitting(true);
			await userService.updateProfile(formData);
			toast.success("プロフィールを更新しました");

			// Refresh auth state to get updated user data
			await checkAuth();
			setIsEditing(false);
		} catch (error) {
			console.error("Profile update error:", error);
			toast.error("プロフィールの更新に失敗しました");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		if (user && user.name) {
			setFormData({
				firstName: user.name.firstName || "",
				lastName: user.name.lastName || "",
				email: user.email || "",
			});
		}
		setIsEditing(false);
	};

	if (!user) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
						<p className="mt-4 text-gray-600 dark:text-gray-400">
							ユーザー情報を読み込み中...
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="space-y-6">
				{/* Header */}
				<div className="flex justify-between items-center">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
							プロフィール設定
						</h2>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							アカウント情報の確認と編集
						</p>
					</div>

					{!isEditing && (
						<button
							onClick={() => setIsEditing(true)}
							className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md hover:from-blue-600 hover:to-indigo-700 transform transition-all duration-200 hover:-translate-y-0.5 flex items-center space-x-2"
						>
							<FiEdit2 className="h-4 w-4" />
							<span>編集する</span>
						</button>
					)}
				</div>

				{/* Profile Content */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Main Profile Form */}
					<div className="lg:col-span-2">
						<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
							<div className="p-6">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
									<FiUser className="h-5 w-5 mr-2 text-blue-500" />
									基本情報
								</h3>

								<form onSubmit={handleSubmit} className="space-y-6">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div>
											<label
												htmlFor="lastName"
												className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
											>
												姓
											</label>
											{isEditing ? (
												<input
													type="text"
													id="lastName"
													name="lastName"
													value={formData.lastName}
													onChange={handleChange}
													className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200"
													required
												/>
											) : (
												<div className="px-3 sm:px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-gray-900 dark:text-white">
													{formData.lastName || "-"}
												</div>
											)}
										</div>

										<div>
											<label
												htmlFor="firstName"
												className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
											>
												名
											</label>
											{isEditing ? (
												<input
													type="text"
													id="firstName"
													name="firstName"
													value={formData.firstName}
													onChange={handleChange}
													className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200"
													required
												/>
											) : (
												<div className="px-3 sm:px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-gray-900 dark:text-white">
													{formData.firstName || "-"}
												</div>
											)}
										</div>
									</div>

									<div>
										<label
											htmlFor="email"
											className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
										>
											メールアドレス
										</label>
										<div className="relative">
											<input
												type="email"
												id="email"
												name="email"
												value={formData.email}
												className="w-full px-3 sm:px-4 py-2.5 pr-10 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 cursor-not-allowed"
												disabled
											/>
											<FiLock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
										</div>
										<p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center">
											<FiAlertCircle className="h-3 w-3 mr-1" />
											メールアドレスの変更はアカウント設定から行ってください
										</p>
									</div>

									{isEditing && (
										<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
											<button
												type="button"
												onClick={handleCancel}
												className="px-3 sm:px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
											>
												キャンセル
											</button>
											<button
												type="submit"
												disabled={isSubmitting}
												className={`flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg ${
													isSubmitting ? "opacity-70 cursor-not-allowed" : ""
												}`}
											>
												<FiSave className="h-4 w-4" />
												<span>{isSubmitting ? "更新中..." : "保存する"}</span>
											</button>
										</div>
									)}
								</form>
							</div>
						</div>
					</div>

					{/* Side Information */}
					<div className="space-y-6">
						{/* Account Security */}
						<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
								<FiShield className="h-5 w-5 mr-2 text-blue-500" />
								アカウントセキュリティ
							</h3>
							<div className="space-y-3">
								<div>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										パスワード
									</p>
									<button className="mt-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
										パスワードを変更
									</button>
								</div>
								<div>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										二段階認証
									</p>
									<button className="mt-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
										設定する
									</button>
								</div>
							</div>
						</div>

						{/* Account Stats */}
						<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
								アカウント情報
							</h3>
							<div className="space-y-3">
								<div className="flex justify-between items-center">
									<span className="text-sm text-gray-600 dark:text-gray-400">
										登録日
									</span>
									<span className="text-sm font-medium text-gray-900 dark:text-white">
										-
									</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-sm text-gray-600 dark:text-gray-400">
										アカウントID
									</span>
									<span className="text-sm font-mono text-gray-900 dark:text-white">
										{user.id
											? String(user.id).padStart(8, "0").slice(0, 8)
											: "-"}
									</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-sm text-gray-600 dark:text-gray-400">
										認証方法
									</span>
									<span className="text-sm font-medium text-gray-900 dark:text-white">
										メール
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Profile;
